/* Momentum Ignition Scanner (Phase 59) — V1.0 paper-only.
   Single page with 6 internal tabs: Dashboard · Live · Trades · Alerts · Performance · Settings. */
import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Activity, AlertCircle, BarChart3, CheckCircle2, MessageCircle, PlayCircle,
  RefreshCw, Save, Settings as SettingsIcon, StopCircle, TrendingUp, Zap,
} from "lucide-react";
import { useAuth } from "../auth/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// --- Shared UI primitives ---
const KpiCard = ({ label, value, sub, tone = "muted", testid }) => (
  <div className="border border-grid p-3 flex flex-col gap-1" data-testid={testid}>
    <div className="text-[9px] uppercase tracking-widest text-muted-3 font-mono-data">{label}</div>
    <div className="text-2xl font-mono-data" style={{ color: `var(--${tone})` }}>{value ?? "—"}</div>
    {sub && <div className="text-[10px] text-muted-3 font-mono-data">{sub}</div>}
  </div>
);

const StatusPill = ({ ok, labelOk = "ACTIVE", labelOff = "STOPPED" }) => (
  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 border text-[9px] uppercase tracking-widest font-mono-data"
        style={{ color: ok ? "var(--bull)" : "var(--bear)",
                  borderColor: ok ? "var(--bull)" : "var(--bear)" }}>
    <span className="w-1.5 h-1.5 rounded-full" style={{
      background: ok ? "var(--bull)" : "var(--bear)" }} />
    {ok ? labelOk : labelOff}
  </span>
);

// --- 1) Dashboard ---
const Dashboard = ({ dashData, refresh }) => {
  if (!dashData) return <div className="text-xs text-muted-3 font-mono-data">Loading…</div>;
  const s = dashData.scanner_state || {};
  return (
    <div className="space-y-4" data-testid="momentum-dashboard">
      <div className="flex items-center justify-between">
        <div className="text-[10px] text-muted-3 font-mono-data uppercase tracking-widest">
          {dashData.day} · Last tick {s.last_tick_at?.slice(11, 19) || "—"}
          {s.last_scan_ms ? ` · ${s.last_scan_ms}ms` : ""}
        </div>
        <div className="flex items-center gap-2">
          <StatusPill ok={s.running && !s.kill_switch} />
          <button onClick={refresh} data-testid="dash-refresh"
                  className="inline-flex items-center gap-1 px-2 py-1 border text-[10px] uppercase font-mono-data hover:bg-white/[0.03]"
                  style={{ borderColor: "var(--grid)", color: "var(--text-muted-2)" }}>
            <RefreshCw size={11} /> Refresh
          </button>
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiCard label="Signals Today" value={dashData.signals_today} tone="bull" testid="kpi-signals" />
        <KpiCard label="Active Trades" value={dashData.active_paper_trades} tone="text" testid="kpi-active" />
        <KpiCard label="Wins" value={dashData.wins} tone="bull" testid="kpi-wins" />
        <KpiCard label="Losses" value={dashData.losses} tone="bear" testid="kpi-losses" />
        <KpiCard label="Win Rate" value={`${dashData.win_rate_pct}%`} tone="text" testid="kpi-winrate" />
        <KpiCard label="Total P&L (₹)"
                  value={dashData.total_pnl >= 0 ? `+${dashData.total_pnl}` : dashData.total_pnl}
                  tone={dashData.total_pnl >= 0 ? "bull" : "bear"} testid="kpi-pnl" />
        <KpiCard label="Avg Hold (min)" value={dashData.avg_hold_minutes} tone="text" testid="kpi-hold" />
        <KpiCard label="Best Stock" value={dashData.best_stock || "—"} sub={`Best score ${dashData.best_score}/100`} tone="bull" testid="kpi-best-stock" />
      </div>
      <div className="border border-grid p-3 text-[10px] font-mono-data text-muted-2">
        <div className="uppercase tracking-widest text-muted-3 mb-1">Scanner Status</div>
        Last tick scanned <span style={{ color: "var(--bull)" }}>{s.last_tick_scanned || 0}</span> symbols ·
        found <span style={{ color: "var(--bull)" }}>{s.last_tick_signals || 0}</span> signals ·
        run every <span style={{ color: "var(--bull)" }}>5 min</span> during market hours (09:15–15:30 IST).
      </div>
    </div>
  );
};

// --- 2b) "Why no signals?" diagnostics — surfaces momentum_scanner.py's
// built-in near-miss/rejection tooling (backend has always computed this;
// the UI never displayed it, so a zero-signal day looked indistinguishable
// from a broken scanner). Self-contained: polls independently of LiveScanner.
const WhyNoSignals = () => {
  const [diag, setDiag] = useState(null);
  const [open, setOpen] = useState(false);
  const load = useCallback(async () => {
    try { const r = await axios.get(`${API}/momentum/diagnostics`); setDiag(r.data); }
    catch { /* diagnostics are best-effort; ignore */ }
  }, []);
  useEffect(() => {
    load();
    const id = setInterval(load, 30000);
    return () => clearInterval(id);
  }, [load]);

  if (!diag) return null;
  const rejections = Object.entries(diag.last_rejection_summary || {}).sort((a, b) => b[1] - a[1]);
  const nearMisses = diag.last_near_misses || [];

  return (
    <div className="border border-grid" data-testid="momentum-why-no-signals">
      <button onClick={() => setOpen((o) => !o)}
              className="w-full flex items-center justify-between px-3 py-2 text-[10px] uppercase tracking-widest font-mono-data text-muted-2 hover:bg-white/[0.02]">
        <span>
          Why no signals? · last scan {diag.last_tick_at ? diag.last_tick_at.slice(11, 19) + " IST" : "never ran"}
          {" · "}{diag.last_tick_scanned ?? 0} scanned · {diag.last_tick_near_misses ?? 0} near-miss
        </span>
        <span>{open ? "▲" : "▼"}</span>
      </button>
      {open && (
        <div className="px-3 pb-3 space-y-3 border-t border-grid">
          {!diag.last_tick_at && (
            <div className="text-[11px] text-muted-3 mt-2">
              The scanner hasn't ticked yet — it only runs 09:15–15:30 IST on weekdays. If this stays empty during
              market hours, the background loop may not be running; check Settings → Scanner Enabled.
            </div>
          )}
          {rejections.length > 0 && (
            <div className="mt-2">
              <div className="text-[9px] uppercase tracking-widest text-muted-3 font-mono-data mb-1">
                Most common rejection reasons (last scan)
              </div>
              <div className="space-y-0.5">
                {rejections.map(([reason, count]) => (
                  <div key={reason} className="flex justify-between text-[11px] font-mono-data text-muted-2">
                    <span>{reason}</span><span>{count}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          {nearMisses.length > 0 ? (
            <div>
              <div className="text-[9px] uppercase tracking-widest text-muted-3 font-mono-data mb-1">
                Closest misses — score ≥ 60, failed on 1-2 gates
              </div>
              <div className="space-y-1">
                {nearMisses.map((m, i) => (
                  <div key={i} className="flex justify-between text-[11px] font-mono-data">
                    <span className="text-muted-1">{m.symbol} <span className="text-muted-3">{m.direction}</span></span>
                    <span className="text-muted-2">score {m.score} · failed: {(m.failing_gates || []).join(", ") || "—"}</span>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            diag.last_tick_at && (
              <div className="text-[11px] text-muted-3 mt-2">
                No candidate came within 15 points of qualifying on the last scan — all 8 gates (price move, OI
                rise, volume, VWAP, EMA20, ADX, RSI band, 15-min breakout) rarely align at once by design. This is
                expected behaviour, not a bug — the threshold can be relaxed in Settings if you'd like more signals.
              </div>
            )
          )}
        </div>
      )}
    </div>
  );
};

// --- 2) Live Scanner ---
const LiveScanner = ({ liveData, refresh, onForceScan, scanning }) => {
  const sigs = liveData?.signals || [];
  return (
    <div className="space-y-3" data-testid="momentum-live">
      <WhyNoSignals />
      <div className="flex items-center justify-between">
        <div className="text-[10px] text-muted-3 font-mono-data uppercase tracking-widest">
          {sigs.length} signal{sigs.length === 1 ? "" : "s"} today (all scores)
        </div>
        <div className="flex items-center gap-2">
          <button onClick={onForceScan} disabled={scanning}
                  data-testid="btn-force-scan"
                  className="inline-flex items-center gap-1 px-3 py-1 border text-[10px] uppercase font-mono-data hover:bg-white/[0.03]"
                  style={{ borderColor: "var(--bull)", color: "var(--bull)" }}>
            {scanning ? <RefreshCw size={11} className="animate-spin" /> : <Zap size={11} />}
            Force Scan
          </button>
          <button onClick={refresh} data-testid="live-refresh"
                  className="inline-flex items-center gap-1 px-2 py-1 border text-[10px] uppercase font-mono-data hover:bg-white/[0.03]"
                  style={{ borderColor: "var(--grid)", color: "var(--text-muted-2)" }}>
            <RefreshCw size={11} /> Refresh
          </button>
        </div>
      </div>
      <div className="border border-grid overflow-x-auto">
        <table className="w-full font-mono-data text-[11px]" data-testid="live-signals-table">
          <thead>
            <tr className="text-muted-3 uppercase tracking-widest text-[9px]">
              <th className="text-left px-2 py-1.5 border-b border-grid">Time</th>
              <th className="text-left px-2 py-1.5 border-b border-grid">Symbol</th>
              <th className="text-right px-2 py-1.5 border-b border-grid">LTP</th>
              <th className="text-right px-2 py-1.5 border-b border-grid">Δ%</th>
              <th className="text-right px-2 py-1.5 border-b border-grid">OI Δ%</th>
              <th className="text-right px-2 py-1.5 border-b border-grid">Vol×</th>
              <th className="text-center px-2 py-1.5 border-b border-grid">VWAP</th>
              <th className="text-center px-2 py-1.5 border-b border-grid">EMA20</th>
              <th className="text-center px-2 py-1.5 border-b border-grid">Breakout</th>
              <th className="text-right px-2 py-1.5 border-b border-grid">Score</th>
              <th className="text-center px-2 py-1.5 border-b border-grid">Signal</th>
            </tr>
          </thead>
          <tbody>
            {sigs.map((s, i) => (
              <tr key={i} className="hover:bg-white/[0.02]">
                <td className="px-2 py-1 text-muted-2">{s.scan_ts?.slice(11, 19) || "—"}</td>
                <td className="px-2 py-1" style={{ color: "var(--bull)" }}>{s.symbol}</td>
                <td className="px-2 py-1 text-right">{s.close}</td>
                <td className="px-2 py-1 text-right"
                     style={{ color: (s.price_change_pct || 0) >= 0 ? "var(--bull)" : "var(--bear)" }}>
                  {(s.price_change_pct || 0).toFixed(2)}%
                </td>
                <td className="px-2 py-1 text-right text-muted-2">
                  {s.gates?.oi_change?.value != null ? `${s.gates.oi_change.value.toFixed(1)}%` : "—"}
                </td>
                <td className="px-2 py-1 text-right">{s.vol_ratio?.toFixed(1) || "—"}×</td>
                <td className="px-2 py-1 text-center text-[10px]"
                     style={{ color: s.gates?.above_vwap?.ok ? "var(--bull)" : "var(--muted-3)" }}>
                  {s.gates?.above_vwap?.ok ? "▲" : "▼"}
                </td>
                <td className="px-2 py-1 text-center text-[10px]"
                     style={{ color: s.gates?.above_ema20?.ok ? "var(--bull)" : "var(--muted-3)" }}>
                  {s.gates?.above_ema20?.ok ? "▲" : "▼"}
                </td>
                <td className="px-2 py-1 text-center text-[10px]"
                     style={{ color: s.gates?.breakout_15m?.ok ? "var(--bull)" : "var(--muted-3)" }}>
                  {s.gates?.breakout_15m?.ok ? "YES" : "no"}
                </td>
                <td className="px-2 py-1 text-right" style={{ color: s.score >= 75 ? "var(--bull)" : "var(--text-muted-2)" }}>
                  {s.score}
                </td>
                <td className="px-2 py-1 text-center text-[10px]"
                     style={{ color: s.signal === "BUY_CE" ? "var(--bull)" : s.signal === "BUY_PE" ? "var(--bear)" : "var(--muted-3)" }}>
                  {s.signal !== "IGNORE" ? s.signal : "—"}
                </td>
              </tr>
            ))}
            {!sigs.length && (
              <tr>
                <td colSpan={11} className="px-2 py-6 text-center text-muted-3">
                  No signals yet today — the scanner ticks every 5 min during market hours (09:15–15:30 IST).
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <div className="text-[10px] text-muted-3 font-mono-data">
        Alerts fire only when Score ≥ 75 AND all gates pass. Table above shows every signal (audit).
      </div>
    </div>
  );
};

// --- 3) Paper Trades ---
const PaperTrades = ({ trades, refresh, period, setPeriod }) => (
  <div className="space-y-3" data-testid="momentum-trades">
    <div className="flex items-center justify-between flex-wrap gap-2">
      <div className="flex items-center gap-1">
        {["today", "week", "month"].map(p => (
          <button key={p} onClick={() => setPeriod(p)}
                  data-testid={`trades-period-${p}`}
                  className="px-2 py-1 border text-[10px] uppercase font-mono-data hover:bg-white/[0.03]"
                  style={{ borderColor: period === p ? "var(--bull)" : "var(--grid)",
                            color: period === p ? "var(--bull)" : "var(--text-muted-2)" }}>
            {p}
          </button>
        ))}
      </div>
      <button onClick={refresh} data-testid="trades-refresh"
              className="inline-flex items-center gap-1 px-2 py-1 border text-[10px] uppercase font-mono-data hover:bg-white/[0.03]"
              style={{ borderColor: "var(--grid)", color: "var(--text-muted-2)" }}>
        <RefreshCw size={11} /> Refresh
      </button>
    </div>
    <div className="border border-grid overflow-x-auto">
      <table className="w-full font-mono-data text-[11px]" data-testid="paper-trades-table">
        <thead>
          <tr className="text-muted-3 uppercase tracking-widest text-[9px]">
            <th className="text-left px-2 py-1.5 border-b border-grid">Entry</th>
            <th className="text-left px-2 py-1.5 border-b border-grid">Symbol</th>
            <th className="text-center px-2 py-1.5 border-b border-grid">Dir</th>
            <th className="text-right px-2 py-1.5 border-b border-grid">Qty</th>
            <th className="text-right px-2 py-1.5 border-b border-grid">Entry ₹</th>
            <th className="text-right px-2 py-1.5 border-b border-grid">Exit ₹</th>
            <th className="text-right px-2 py-1.5 border-b border-grid">P&L ₹</th>
            <th className="text-right px-2 py-1.5 border-b border-grid">P&L %</th>
            <th className="text-center px-2 py-1.5 border-b border-grid">Status</th>
            <th className="text-center px-2 py-1.5 border-b border-grid">Exit</th>
          </tr>
        </thead>
        <tbody>
          {(trades || []).map((t, i) => (
            <tr key={i} className="hover:bg-white/[0.02]">
              <td className="px-2 py-1 text-muted-2">{t.entry_ts?.slice(11, 19)}</td>
              <td className="px-2 py-1" style={{ color: "var(--bull)" }}>{t.symbol}</td>
              <td className="px-2 py-1 text-center"
                   style={{ color: t.direction === "LONG" ? "var(--bull)" : "var(--bear)" }}>
                {t.direction === "LONG" ? "L" : "S"}
              </td>
              <td className="px-2 py-1 text-right">{t.quantity}</td>
              <td className="px-2 py-1 text-right">{t.entry_price}</td>
              <td className="px-2 py-1 text-right">{t.exit_price || t.last_ltp || "—"}</td>
              <td className="px-2 py-1 text-right"
                   style={{ color: (t.pnl_abs || 0) >= 0 ? "var(--bull)" : "var(--bear)" }}>
                {t.pnl_abs != null ? (t.pnl_abs >= 0 ? "+" : "") + t.pnl_abs : "—"}
              </td>
              <td className="px-2 py-1 text-right"
                   style={{ color: (t.pnl_pct || 0) >= 0 ? "var(--bull)" : "var(--bear)" }}>
                {t.pnl_pct != null ? `${t.pnl_pct >= 0 ? "+" : ""}${t.pnl_pct}%` : "—"}
              </td>
              <td className="px-2 py-1 text-center text-[10px]"
                   style={{ color: t.status === "OPEN" ? "var(--bull)" : "var(--text-muted-2)" }}>
                {t.status}
              </td>
              <td className="px-2 py-1 text-center text-[10px] text-muted-2">
                {t.exit_reason || (t.status === "OPEN" ? "—" : "")}
              </td>
            </tr>
          ))}
          {(!trades || !trades.length) && (
            <tr>
              <td colSpan={10} className="px-2 py-6 text-center text-muted-3">
                No paper trades in this window.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  </div>
);

// --- 4) Telegram Alerts ---
const Alerts = ({ alerts, refresh }) => {
  const [filter, setFilter] = useState("ALL"); // ALL | SIGNAL | REVERSAL
  const shown = (alerts || []).filter(a => {
    if (filter === "ALL") return true;
    if (filter === "REVERSAL") return a.type === "REVERSAL";
    return a.type !== "REVERSAL"; // SIGNAL = default alerts w/ no type or SIGNAL
  });
  return (
    <div className="space-y-3" data-testid="momentum-alerts">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="text-[10px] text-muted-3 font-mono-data uppercase tracking-widest">
          {shown.length} alert{shown.length === 1 ? "" : "s"} · dedupe: 20-min cooldown per symbol
          (unless score improves ≥ 10) · one reversal alert per open trade
        </div>
        <div className="flex items-center gap-1">
          {["ALL", "SIGNAL", "REVERSAL"].map(f => (
            <button key={f} onClick={() => setFilter(f)}
                    data-testid={`alerts-filter-${f.toLowerCase()}`}
                    className="px-2 py-1 border text-[10px] uppercase font-mono-data hover:bg-white/[0.03]"
                    style={{ borderColor: filter === f ? "var(--bull)" : "var(--grid)",
                              color: filter === f ? "var(--bull)" : "var(--text-muted-2)" }}>
              {f}
            </button>
          ))}
          <button onClick={refresh} data-testid="alerts-refresh"
                  className="inline-flex items-center gap-1 px-2 py-1 border text-[10px] uppercase font-mono-data hover:bg-white/[0.03]"
                  style={{ borderColor: "var(--grid)", color: "var(--text-muted-2)" }}>
            <RefreshCw size={11} /> Refresh
          </button>
        </div>
      </div>
      <div className="space-y-1">
        {shown.map((a, i) => {
          const isRev = a.type === "REVERSAL";
          return (
            <div key={i} className="border p-2 font-mono-data text-[11px] flex items-start gap-2"
                  style={{ borderColor: isRev ? "var(--bear)" : "var(--grid)" }}>
              <MessageCircle size={12}
                              style={{ color: !a.sent ? "var(--bear)" : (isRev ? "var(--warn)" : "var(--bull)") }}
                              className="mt-0.5" />
              <div className="flex-1">
                <div className="flex items-center gap-2 text-[10px] text-muted-3">
                  <span>{a.sent_at?.slice(11, 19)}</span>
                  <span className="px-1 border text-[9px] uppercase tracking-widest"
                         style={{ color: isRev ? "var(--bear)" : "var(--bull)",
                                   borderColor: isRev ? "var(--bear)" : "var(--bull)" }}>
                    {isRev ? "REVERSAL" : "SIGNAL"}
                  </span>
                  <span style={{ color: "var(--bull)" }}>{a.symbol}</span>
                  <span>{a.signal}</span>
                  {a.score != null && <span>Score {a.score}</span>}
                  {a.passing != null && <span>{a.passing}/5 checks</span>}
                  {!a.sent && <span style={{ color: "var(--bear)" }}>· FAILED</span>}
                </div>
                <pre className="whitespace-pre-wrap text-[10px] text-muted-2 mt-0.5">{a.message}</pre>
              </div>
            </div>
          );
        })}
        {!shown.length && (
          <div className="border border-grid p-6 text-center text-muted-3 text-[11px] font-mono-data">
            No {filter === "ALL" ? "" : filter.toLowerCase() + " "}alerts fired yet.
          </div>
        )}
      </div>
    </div>
  );
};

// --- 5) Performance ---
const Performance = ({ data, period, setPeriod, refresh }) => (
  <div className="space-y-3" data-testid="momentum-performance">
    <div className="flex items-center justify-between flex-wrap gap-2">
      <div className="flex items-center gap-1">
        {["today", "week", "month"].map(p => (
          <button key={p} onClick={() => setPeriod(p)}
                  data-testid={`perf-period-${p}`}
                  className="px-2 py-1 border text-[10px] uppercase font-mono-data hover:bg-white/[0.03]"
                  style={{ borderColor: period === p ? "var(--bull)" : "var(--grid)",
                            color: period === p ? "var(--bull)" : "var(--text-muted-2)" }}>
            {p}
          </button>
        ))}
      </div>
      <button onClick={refresh} data-testid="perf-refresh"
              className="inline-flex items-center gap-1 px-2 py-1 border text-[10px] uppercase font-mono-data hover:bg-white/[0.03]"
              style={{ borderColor: "var(--grid)", color: "var(--text-muted-2)" }}>
        <RefreshCw size={11} /> Refresh
      </button>
    </div>
    {data ? (
      <>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <KpiCard label="Trades" value={data.total_trades} tone="text" testid="perf-total" />
          <KpiCard label="Win Rate" value={`${data.win_rate_pct}%`} tone="text" testid="perf-wr" />
          <KpiCard label="Total P&L (₹)"
                    value={data.total_pnl >= 0 ? `+${data.total_pnl}` : data.total_pnl}
                    tone={data.total_pnl >= 0 ? "bull" : "bear"} testid="perf-pnl" />
          <KpiCard label="Avg Hold (min)" value={data.avg_hold_minutes} tone="text" testid="perf-hold" />
          <KpiCard label="Avg Win (₹)" value={data.avg_profit >= 0 ? `+${data.avg_profit}` : data.avg_profit}
                    tone="bull" testid="perf-avgwin" />
          <KpiCard label="Avg Loss (₹)" value={data.avg_loss} tone="bear" testid="perf-avgloss" />
          <KpiCard label="Best Stock" value={data.best_stock || "—"} tone="bull" testid="perf-best" />
          <KpiCard label="Worst Stock" value={data.worst_stock || "—"} tone="bear" testid="perf-worst" />
        </div>
        {data.daily_pnl?.length ? (
          <div className="border border-grid p-3">
            <div className="text-[9px] uppercase tracking-widest text-muted-3 font-mono-data mb-2">Daily P&L</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-1 font-mono-data text-[11px]">
              {data.daily_pnl.map(d => (
                <div key={d.day} className="flex justify-between">
                  <span className="text-muted-2">{d.day}</span>
                  <span style={{ color: d.pnl_abs >= 0 ? "var(--bull)" : "var(--bear)" }}>
                    {d.pnl_abs >= 0 ? "+" : ""}{d.pnl_abs}
                  </span>
                </div>
              ))}
            </div>
          </div>
        ) : null}
      </>
    ) : <div className="text-xs text-muted-3 font-mono-data">Loading…</div>}
  </div>
);

// --- 6) Settings ---
// Field metadata: [key, label, type, min, max, step, unit]
const SETTING_FIELDS = [
  ["price_change_threshold_pct", "Price Δ Threshold (5-min)", "num", 0.1, 10, 0.1, "%"],
  ["oi_increase_threshold_pct",  "OI Δ Threshold",             "num", 0,    50, 0.5, "%"],
  ["volume_multiplier",          "Volume Multiplier",           "num", 1,    10, 0.1, "×"],
  ["adx_threshold",              "ADX Threshold",               "num", 10,   60, 1,   ""],
  ["rsi_min",                    "RSI Min",                     "num", 30,   90, 1,   ""],
  ["rsi_max",                    "RSI Max",                     "num", 30,   90, 1,   ""],
  ["breakout_lookback_minutes",  "Breakout Lookback",           "num", 5,    60, 5,   "min"],
  ["score_alert_threshold",      "Alert Score Threshold",       "num", 40,  100, 1,   ""],
  ["target_pct",                 "Target",                      "num", 0.5,  20, 0.1, "%"],
  ["stop_loss_pct",              "Stop Loss",                   "num", 0.5,  10, 0.1, "%"],
  ["trailing_stop_pct",          "Trailing Stop",               "num", 0.1,   5, 0.1, "%"],
  ["time_exit_minutes",          "Time Exit",                   "num", 5,   240, 5,   "min"],
  ["paper_capital_per_trade",    "Paper Capital / Trade",       "num", 1000, 1000000, 1000, "₹"],
  ["alert_cooldown_minutes",     "Alert Cooldown",              "num", 1,   120, 1,   "min"],
  ["score_improvement_bump",     "Score Bump for Re-alert",     "num", 1,    50, 1,   ""],
  ["scanner_enabled",            "Scanner Enabled",             "bool"],
  ["alerts_enabled",             "Alerts Enabled",              "bool"],
  ["telegram_enabled",           "Telegram Enabled",            "bool"],
  ["shorts_enabled",             "Shorts Enabled",              "bool"],
];

const Settings = ({ config, save, isAdmin, scannerState, onStart, onStop }) => {
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (config?.values) setDraft({ ...config.values }); }, [config?.values]);

  if (!draft) return <div className="text-xs text-muted-3 font-mono-data">Loading…</div>;
  const commit = async () => {
    setSaving(true);
    try {
      // Only send the 19 rendered keys — never overwrite hidden section13 keys
      // (e.g. eod_hh, market_open_hh) that aren't in the settings grid.
      const allowed = SETTING_FIELDS.map(([k]) => k);
      const filtered = Object.fromEntries(
        allowed.filter(k => draft[k] !== undefined).map(k => [k, draft[k]]));
      await save(filtered);
      toast.success("Momentum settings saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };
  const reset = () => setDraft({ ...(config?.defaults || {}) });

  return (
    <div className="space-y-4" data-testid="momentum-settings">
      <div className="border border-grid p-3 flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2 font-mono-data text-[11px]">
          <span className="text-muted-3 uppercase tracking-widest">Scanner Control</span>
          <StatusPill ok={scannerState?.running && !scannerState?.kill_switch} />
          <span className="text-muted-3 text-[10px]">
            Last tick: {scannerState?.last_tick_at?.slice(11, 19) || "—"}
          </span>
        </div>
        {isAdmin && (
          <div className="flex items-center gap-2 flex-wrap">
            <button onClick={onStart} data-testid="settings-start"
                    className="inline-flex items-center gap-1 px-3 py-1 border text-[10px] uppercase font-mono-data hover:bg-white/[0.03]"
                    style={{ borderColor: "var(--bull)", color: "var(--bull)" }}>
              <PlayCircle size={12} /> Start
            </button>
            <button onClick={onStop} data-testid="settings-stop"
                    className="inline-flex items-center gap-1 px-3 py-1 border text-[10px] uppercase font-mono-data hover:bg-white/[0.03]"
                    style={{ borderColor: "var(--bear)", color: "var(--bear)" }}>
              <StopCircle size={12} /> Stop (Kill Switch)
            </button>
            <span className="text-muted-3 text-[10px] px-1">·</span>
            <button onClick={reset} data-testid="settings-reset"
                    className="inline-flex items-center gap-1 px-3 py-1 border text-[10px] uppercase font-mono-data hover:bg-white/[0.03]"
                    style={{ borderColor: "var(--grid)", color: "var(--text-muted-2)" }}>
              Reset
            </button>
            <button onClick={commit} disabled={saving} data-testid="settings-save"
                    className="inline-flex items-center gap-1 px-4 py-1 border text-[10px] uppercase font-mono-data hover:bg-white/[0.03]"
                    style={{ borderColor: "var(--bull)", color: "var(--bull)" }}>
              {saving ? <RefreshCw size={11} className="animate-spin" /> : <Save size={11} />}
              Save
            </button>
          </div>
        )}
      </div>

      {!isAdmin && (
        <div className="border p-3 text-[11px] font-mono-data flex items-center gap-2"
              style={{ borderColor: "var(--warn)", color: "var(--warn)" }}>
          <AlertCircle size={12} /> Read-only view. Only admins can save changes.
        </div>
      )}

      <div className="border border-grid p-3 grid grid-cols-1 md:grid-cols-2 gap-3">
        {SETTING_FIELDS.map(([k, label, type, min, max, step, unit]) => (
          <div key={k} className="flex flex-col gap-1" data-testid={`setting-${k}`}>
            <div className="text-[10px] uppercase tracking-widest text-muted-3 font-mono-data">{label}</div>
            {type === "bool" ? (
              <label className="flex items-center gap-2 font-mono-data text-[11px] cursor-pointer">
                <input type="checkbox"
                        checked={!!Number(draft[k])}
                        disabled={!isAdmin}
                        onChange={(e) => setDraft({ ...draft, [k]: e.target.checked ? 1 : 0 })}
                        data-testid={`setting-${k}-input`} />
                <span style={{ color: Number(draft[k]) ? "var(--bull)" : "var(--muted-3)" }}>
                  {Number(draft[k]) ? "ON" : "OFF"}
                </span>
              </label>
            ) : (
              <input type="number"
                      min={min} max={max} step={step}
                      value={draft[k]}
                      disabled={!isAdmin}
                      onChange={(e) => setDraft({ ...draft, [k]: parseFloat(e.target.value) })}
                      data-testid={`setting-${k}-input`}
                      className="bg-transparent border border-grid px-2 py-1 text-xs font-mono-data w-full" />
            )}
            {unit && type !== "bool" && (
              <div className="text-[9px] text-muted-3 font-mono-data">{unit}</div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};


// --- Container / router ---
const TABS = [
  { id: "dashboard",   label: "Dashboard",   icon: BarChart3 },
  { id: "live",        label: "Live Scanner", icon: TrendingUp },
  { id: "trades",      label: "Paper Trades", icon: Activity },
  { id: "alerts",      label: "Telegram Alerts", icon: MessageCircle },
  { id: "performance", label: "Performance", icon: CheckCircle2 },
  { id: "settings",    label: "Settings",    icon: SettingsIcon },
];

const MomentumScannerPage = () => {
  const [tab, setTab] = useState("dashboard");
  const [dashData, setDashData]           = useState(null);
  const [liveData, setLiveData]           = useState(null);
  const [trades, setTrades]               = useState([]);
  const [tradesPeriod, setTradesPeriod]   = useState("today");
  const [alerts, setAlerts]               = useState([]);
  const [perfData, setPerfData]           = useState(null);
  const [perfPeriod, setPerfPeriod]       = useState("today");
  const [config, setConfig]               = useState(null);
  const [scanning, setScanning]           = useState(false);
  const { isAdmin } = useAuth();

  const loadDash    = useCallback(async () => {
    try { const r = await axios.get(`${API}/momentum/dashboard`); setDashData(r.data); }
    catch (e) { toast.error("Dashboard load failed"); }
  }, []);
  const loadLive    = useCallback(async () => {
    try { const r = await axios.get(`${API}/momentum/live`); setLiveData(r.data); }
    catch { /* silent */ }
  }, []);
  const loadTrades  = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/momentum/paper-trades`, { params: { period: tradesPeriod, limit: 200 } });
      setTrades(r.data.trades || []);
    } catch { /* silent */ }
  }, [tradesPeriod]);
  const loadAlerts  = useCallback(async () => {
    try { const r = await axios.get(`${API}/momentum/alerts`, { params: { limit: 100 } });
          setAlerts(r.data.alerts || []); }
    catch { /* silent */ }
  }, []);
  const loadPerf    = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/momentum/performance`, { params: { period: perfPeriod } });
      setPerfData(r.data);
    } catch { /* silent */ }
  }, [perfPeriod]);
  const loadConfig  = useCallback(async () => {
    try { const r = await axios.get(`${API}/momentum/config`); setConfig(r.data); }
    catch { /* silent */ }
  }, []);

  // Initial load
  useEffect(() => { loadDash(); loadConfig(); }, [loadDash, loadConfig]);
  // Tab lazy loads
  useEffect(() => {
    if (tab === "dashboard")   loadDash();
    if (tab === "live")        loadLive();
    if (tab === "trades")      loadTrades();
    if (tab === "alerts")      loadAlerts();
    if (tab === "performance") loadPerf();
    if (tab === "settings")    loadConfig();
  }, [tab, loadDash, loadLive, loadTrades, loadAlerts, loadPerf, loadConfig]);
  // Auto-refresh dashboard + live every 15s while their tab is active
  useEffect(() => {
    if (tab !== "dashboard" && tab !== "live") return;
    const id = setInterval(() => {
      if (tab === "dashboard") loadDash();
      if (tab === "live")      loadLive();
    }, 15000);
    return () => clearInterval(id);
  }, [tab, loadDash, loadLive]);

  const forceScan = async () => {
    setScanning(true);
    try {
      await axios.post(`${API}/momentum/scan`, {}, { params: { force: true } });
      await loadLive();
      toast.success("Force scan complete");
    } catch (e) { toast.error("Scan failed"); }
    finally { setScanning(false); }
  };
  const saveConfig = async (values) => {
    await axios.post(`${API}/admin/config/section13`, { values, enabled: true });
    await loadConfig();
  };
  const startScanner = async () => {
    try { await axios.post(`${API}/momentum/scanner/start`);
          toast.success("Scanner started"); loadDash(); loadConfig(); }
    catch { toast.error("Admin only — start requires admin session"); }
  };
  const stopScanner = async () => {
    try { await axios.post(`${API}/momentum/scanner/stop`);
          toast.success("Scanner kill-switch engaged"); loadDash(); loadConfig(); }
    catch { toast.error("Admin only — stop requires admin session"); }
  };

  return (
    <section className="pt-6 pb-16 px-6 md:px-10 xl:px-14" data-testid="momentum-page">
      <div className="text-[11px] uppercase tracking-[0.3em] text-muted-3 font-mono-data flex items-center gap-2">
        <Zap size={12} style={{ color: "var(--bull)" }} /> Institutional Momentum · Paper-Trading V1.0
      </div>
      <h1 className="mt-1 font-heading text-4xl sm:text-5xl lg:text-6xl">Momentum Scanner</h1>
      <p className="mt-2 text-sm text-muted-2 max-w-3xl">
        Detects stocks where institutions are aggressively building positions —
        Price · OI · Volume · VWAP · EMA20 · ADX · RSI · 15-min breakout all
        must align, and Momentum Score must clear <strong>75</strong>. Signals
        fire Telegram alerts and open a ₹50k paper trade on the underlying stock.
      </p>

      {/* Tabs */}
      <div className="mt-6 flex flex-wrap gap-1 border-b border-grid" role="tablist">
        {TABS.map(t => {
          const Icon = t.icon;
          const active = tab === t.id;
          return (
            <button key={t.id} onClick={() => setTab(t.id)}
                    data-testid={`momentum-tab-${t.id}`}
                    role="tab"
                    aria-selected={active}
                    className="inline-flex items-center gap-1.5 px-3 py-2 -mb-px border-b-2 text-[11px] uppercase font-mono-data tracking-widest hover:bg-white/[0.02]"
                    style={{ borderColor: active ? "var(--bull)" : "transparent",
                              color: active ? "var(--bull)" : "var(--text-muted-2)" }}>
              <Icon size={12} /> {t.label}
            </button>
          );
        })}
      </div>

      <div className="mt-4">
        {tab === "dashboard"   && <Dashboard dashData={dashData} refresh={loadDash} />}
        {tab === "live"        && <LiveScanner liveData={liveData} refresh={loadLive}
                                                onForceScan={forceScan} scanning={scanning} />}
        {tab === "trades"      && <PaperTrades trades={trades} refresh={loadTrades}
                                                period={tradesPeriod} setPeriod={setTradesPeriod} />}
        {tab === "alerts"      && <Alerts alerts={alerts} refresh={loadAlerts} />}
        {tab === "performance" && <Performance data={perfData} period={perfPeriod}
                                                 setPeriod={setPerfPeriod} refresh={loadPerf} />}
        {tab === "settings"    && <Settings config={config} save={saveConfig}
                                              isAdmin={isAdmin}
                                              scannerState={dashData?.scanner_state}
                                              onStart={startScanner} onStop={stopScanner} />}
      </div>
    </section>
  );
};

export default MomentumScannerPage;